import { z } from 'zod';
import type { Parameters, RequestBody, RequestForm, Response } from '../../../../../types/typeHelper';
import { useQueryParams } from '../../../../design-system/hooks/use-query-params';
import { createFileFromUrl } from '../../../../helper/fileHelper';
import { TICKET_DASHBOARD_TABS, TicketDashboardTabNames } from './../../../../slices/tickets/ticketsConstants';
import { reactQueryClient, useApiClient } from '../../client';
import { useUploadETSTicketAttachmentsMutation } from './tickets-api';

export enum TicketsClientQueryKeys {
  GET_ETS_TICKETS = 'GET_ETS_TICKETS',
  GET_ETS_TICKETS_CREATORS = 'GET_ETS_TICKETS_CREATORS',
  GET_ETS_TICKETS_INFINITE = 'GET_ETS_TICKETS_INFINITE',
  GET_ETS_TICKET_RATING = 'GET_ETS_TICKET_RATING',
  CREATE_ETS_TICKET = 'CREATE_ETS_TICKET',
  ADD_ETS_TICKET_RATING = 'ADD_ETS_TICKET_RATING',
  PATCH_ETS_TICKET_STATUS = 'PATCH_ETS_TICKET_STATUS',
  DELETE_ETS_TICKET_COMMENT = 'DELETE_ETS_TICKET_COMMENT',
  DELETE_ETS_TICKET_NOTIFICATION = 'DELETE_ETS_TICKET_NOTIFICATION',
  GET_ETS_TICKET_PARTS = 'GET_ETS_TICKET_PARTS',
  GET_ETS_TICKET_NOTIFICATIONS = 'GET_ETS_TICKET_NOTIFICATIONS',
  GET_ETS_TICKET = 'GET_ETS_TICKET',
  GET_ETS_TICKET_ATTACHMENTS = 'GET_ETS_TICKET_ATTACHMENTS',
  GET_ETS_TICKET_COMMENTS = 'GET_ETS_TICKET_COMMENTS',
  UPLOAD_ETS_TICKET_ATTACHMENTS = 'UPLOAD_ETS_TICKET_ATTACHMENTS',
  ADD_ETS_TICKET_COMMENT = 'ADD_ETS_TICKET_COMMENT',
  DELETE_ETS_TICKET_RATING = 'DELETE_ETS_TICKET_RATING',
  GET_TICKET_INFORMATION = 'GET_TICKET_INFORMATION',
  GET_ETS_TICKETS_COLLEAGUES = 'GET_ETS_TICKETS_COLLEAGUES',
}

export interface ETSTicketsFilters {
  tab: TicketDashboardTabNames | undefined;
  pageIndex: Parameters<'/chat/ets/tickets', 'get'>['query']['index'];
  pageSize: Parameters<'/chat/ets/tickets', 'get'>['query']['size'];
  sortedColumnId: Parameters<'/chat/ets/tickets', 'get'>['query']['sorted_column_id'];
  sortOrder: Parameters<'/chat/ets/tickets', 'get'>['query']['sort_order'];
  startDate: Parameters<'/chat/ets/tickets', 'get'>['query']['start_date'];
  endDate: Parameters<'/chat/ets/tickets', 'get'>['query']['end_date'];
  erpCustomerIds: Parameters<'/chat/ets/tickets', 'get'>['query']['erp_customer_ids'];
  search: Parameters<'/chat/ets/tickets', 'get'>['query']['search'];
  channelId: Parameters<'/chat/ets/tickets', 'get'>['query']['channel_id'];
  creatorIds: Parameters<'/chat/ets/tickets', 'get'>['query']['creator_ids'];
}

export const ETS_TICKETS_OVERVIEW_QUERY_PARAMS = ['pageSize', 'pageIndex'] as const;
export const ETS_TICKETS_DASHBOARD_QUERY_PARAMS = [
  ...ETS_TICKETS_OVERVIEW_QUERY_PARAMS,
  'sortedColumnId',
  'sortOrder',
  'tab',
  'startDate',
  'endDate',
  'erpCustomerIds',
  'search',
  'channelId',
  'creatorIds',
] as const;

export const ETS_TICKETS_INFINITE_QUERY_PAGE_SIZE = 10;

export const ETS_TICKETS_CRAFTSMEN_QUERY: Partial<Parameters<'/chat/ets/tickets', 'get'>['query']> = {
  channel_id: undefined,
} as const;

export const INITIAL_ETS_TICKETS_DASHBOARD_PAGINATION_QUERY_PARAMS: Partial<Record<keyof ETSTicketsFilters, string>> = {
  pageIndex: '0',
  pageSize: '10',
} as const;

export const INITIAL_ETS_TICKETS_OVERVIEW_PAGINATION_QUERY_PARAMS: Partial<Record<keyof ETSTicketsFilters, string>> = {
  pageIndex: '0',
  pageSize: '1000',
} as const;

export const useETSTicketsQueryParams = () => {
  const { queryParams, setQueryParams } = useQueryParams<(typeof ETS_TICKETS_DASHBOARD_QUERY_PARAMS)[number]>(
    ETS_TICKETS_DASHBOARD_QUERY_PARAMS,
  );

  const filters = generateETSTicketsFilters({
    pageIndex: queryParams.pageIndex ? Number(queryParams.pageIndex) : undefined,
    pageSize: queryParams.pageSize ? Number(queryParams.pageSize) : undefined,
    sortedColumnId: queryParams.sortedColumnId,
    sortOrder: queryParams.sortOrder ? Number(queryParams.sortOrder) : undefined,
    tab: z.nativeEnum(TicketDashboardTabNames).optional().parse(queryParams.tab),
    startDate: queryParams.startDate,
    endDate: queryParams.endDate,
    erpCustomerIds: queryParams.erpCustomerIds,
    search: queryParams.search,
    channelId: queryParams.channelId,
    creatorIds: queryParams.creatorIds,
  });

  return {
    etsTicketsfilters: filters,
    setETSTicketsQueryParams: setQueryParams,
  };
};

export const useSelectedETSTicketIdQueryParams = () => {
  const { queryParams, setQueryParams } = useQueryParams(['ticketId']);

  return {
    selectedTicketId: queryParams.ticketId,
    setSelectedTicketId: (ticketId: string | undefined) => setQueryParams({ ticketId }),
  };
};

export type FetchETSTicketsResponseData = Response<'/chat/ets/tickets', 'get'>;
export type FetchETSTicketRatingResponseData = Response<'/chat/ets/tickets/{ticket_id}/rating', 'get'>;
export type FetchETSTicketNotificationsResponseData = Response<'/chat/current-user/ets-notifications', 'get'>;
export type FetchETSTicketCommentsResponseData = Response<'/chat/ets/tickets/{ticket_id}/comments', 'get'>;
export type FetchETSTicketAttachmentsResponseData = Response<'/chat/ets/tickets/{ticket_id}/attachments', 'get'>;
export type FetchETSTicketPartsResponseData = Response<'/chat/ets/tickets/{ticket_id}/parts', 'get'>;
export type FetchETSTicketResponseData = Response<'/chat/ets/tickets/{ticket_id}', 'get'>;
export type FetchETSTicketInformationResponseData = Response<'/chat/ets/tickets/v2/{ticket_id}', 'get'>;
export type FetchETSTicketsColleaguesResponseData = Response<'/chat/ets/colleagues', 'get'>;

export const getGetETSTicketsInfiniteQueryKey = (
  query: Omit<Parameters<'/chat/ets/tickets', 'get'>['query'], 'size' | 'index'>,
) => [TicketsClientQueryKeys.GET_ETS_TICKETS_INFINITE, query];

export const getGetETSTicketsQueryKey = (query: Parameters<'/chat/ets/tickets', 'get'>['query']) => [
  TicketsClientQueryKeys.GET_ETS_TICKETS,
  query,
];

export const getGetETSTicketsCreatorsQueryKey = () => [TicketsClientQueryKeys.GET_ETS_TICKETS_CREATORS];

export const getGetETSTicketRatingQueryKey = (
  path: Parameters<'/chat/ets/tickets/{ticket_id}/rating', 'get'>['path'],
) => [TicketsClientQueryKeys.GET_ETS_TICKET_RATING, path];

export const getGetETSTicketPartsQueryKey = (
  path: Parameters<'/chat/ets/tickets/{ticket_id}/parts', 'get'>['path'],
) => [TicketsClientQueryKeys.GET_ETS_TICKET_PARTS, path];

export const getGetETSTicketNotificationsQueryKey = () => [TicketsClientQueryKeys.GET_ETS_TICKET_NOTIFICATIONS];

export const getGetETSTicketQueryKey = (path: Parameters<'/chat/ets/tickets/{ticket_id}', 'get'>['path']) => [
  TicketsClientQueryKeys.GET_ETS_TICKET,
  path,
];

export const getGetETSTicketsColleaguesQueryKey = () => [TicketsClientQueryKeys.GET_ETS_TICKETS_COLLEAGUES];

export const getGetETSTicketAttachmentsQueryKey = (
  path: Parameters<'/chat/ets/tickets/{ticket_id}/attachments', 'get'>['path'],
) => [TicketsClientQueryKeys.GET_ETS_TICKET_ATTACHMENTS, path];

export const getGetETSTicketCommentsQueryKey = (
  path: Parameters<'/chat/ets/tickets/{ticket_id}/comments', 'get'>['path'],
) => [TicketsClientQueryKeys.GET_ETS_TICKET_COMMENTS, path];

export const getGetETSTicketInformationQueryKey = (
  path: Parameters<'/chat/ets/tickets/v2/{ticket_id}', 'get'>['path'],
) => [TicketsClientQueryKeys.GET_TICKET_INFORMATION, path];

export const useETSTicketsApiHelper = () => {
  const client = useApiClient();
  return {
    createETSTicket: async ({ body }: { body: RequestBody<'/chat/ets/tickets', 'post'> }) => {
      return client.POST('/chat/ets/tickets', {
        body,
      });
    },
    addETSTicketRating: async ({
      path,
      body,
    }: {
      body: RequestBody<'/chat/ets/tickets/{ticket_id}/rating', 'post'>;
      path: Parameters<'/chat/ets/tickets/{ticket_id}/rating', 'post'>['path'];
    }) => {
      return client.POST('/chat/ets/tickets/{ticket_id}/rating', {
        body,
        params: {
          path,
        },
      });
    },
    patchETSTicketStatus: async ({
      path,
    }: {
      path: Parameters<'/chat/ets/tickets/{ticket_id}/status', 'patch'>['path'];
    }) => {
      return client.PATCH('/chat/ets/tickets/{ticket_id}/status', {
        params: {
          path,
        },
      });
    },
    deleteETSTicketComment: async ({
      path,
    }: {
      path: Parameters<'/chat/ets/tickets/{ticket_id}/comments/{comment_id}', 'delete'>['path'];
    }) => {
      return client.DELETE('/chat/ets/tickets/{ticket_id}/comments/{comment_id}', {
        params: {
          path,
        },
      });
    },
    deleteETSTicketNotification: async ({
      path,
    }: {
      path: Parameters<'/chat/ets/notifications/{id}', 'delete'>['path'];
    }) => {
      return client.DELETE('/chat/ets/notifications/{id}', {
        params: {
          path,
        },
      });
    },
    uploadETSTicketAttachments: async ({
      path,
      form,
    }: {
      path: Parameters<'/chat/ets/tickets/{ticket_id}/attachments', 'post'>['path'];
      form: RequestForm<'/chat/ets/tickets/{ticket_id}/attachments', 'post'>;
    }) => {
      return client.POST('/chat/ets/tickets/{ticket_id}/attachments', {
        params: {
          path,
        },
        body: form,
        bodySerializer(body) {
          const fd = new FormData();
          for (const f of body.file) {
            fd.append('file', f);
          }
          return fd;
        },
      });
    },
    addETSTicketComment: async ({
      path,
      body,
    }: {
      body: RequestBody<'/chat/ets/tickets/{ticket_id}/comments', 'post'>;
      path: Parameters<'/chat/ets/tickets/{ticket_id}/comments', 'post'>['path'];
    }) => {
      return client.POST('/chat/ets/tickets/{ticket_id}/comments', {
        params: {
          path,
        },
        body,
      });
    },
    deleteETSTicketRating: async ({
      path,
    }: {
      path: Parameters<'/chat/ets/tickets/{ticket_id}/rating', 'delete'>['path'];
    }) => {
      return client.DELETE('/chat/ets/tickets/{ticket_id}/rating', {
        params: {
          path,
        },
      });
    },
  };
};

export const convertETSTicketsFiltersToQuery = (
  filters: ETSTicketsFilters,
): Parameters<'/chat/ets/tickets', 'get'>['query'] => {
  const status = filters.tab ? TICKET_DASHBOARD_TABS[filters.tab].status : undefined;
  return {
    status,
    additional_status_filter:
      filters.tab === TicketDashboardTabNames.NOT_LISTED ? TicketDashboardTabNames.NOT_LISTED : undefined,
    index: filters.pageIndex,
    size: filters.pageSize,
    sorted_column_id: filters.sortedColumnId,
    sort_order: filters.sortOrder,
    start_date: filters.startDate,
    end_date: filters.endDate,
    erp_customer_ids: filters.erpCustomerIds,
    search: filters.search,
    channel_id: filters.channelId,
    creator_ids: filters.creatorIds === 'None' ? undefined : filters.creatorIds,
  };
};

export const generateETSTicketsFilters = (filters: Partial<ETSTicketsFilters>): ETSTicketsFilters => {
  return {
    pageIndex: 0,
    pageSize: 10,
    sortedColumnId: undefined,
    sortOrder: undefined,
    tab: undefined,
    startDate: undefined,
    endDate: undefined,
    erpCustomerIds: undefined,
    search: undefined,
    channelId: undefined,
    creatorIds: undefined,
    ...filters,
  };
};

interface GenerateETSTicketsCraftsmenInfiniteQueryProps {
  creatorIds: Parameters<'/chat/ets/tickets', 'get'>['query']['creator_ids'];
}

export const generateETSTicketsCraftsmenInfiniteQuery = (
  props: GenerateETSTicketsCraftsmenInfiniteQueryProps,
): Omit<Parameters<'/chat/ets/tickets', 'get'>['query'], 'size' | 'index'> => {
  return {
    ...ETS_TICKETS_CRAFTSMEN_QUERY,
    creator_ids: props.creatorIds,
  };
};

export const refetchETSTicketDetails = (ticketId: string) => {
  reactQueryClient.invalidateQueries({
    queryKey: getGetETSTicketQueryKey({ ticket_id: ticketId }),
  });
  reactQueryClient.invalidateQueries({
    queryKey: getGetETSTicketAttachmentsQueryKey({ ticket_id: ticketId }),
  });
  reactQueryClient.invalidateQueries({
    queryKey: getGetETSTicketCommentsQueryKey({ ticket_id: ticketId }),
  });
  reactQueryClient.invalidateQueries({
    queryKey: getGetETSTicketPartsQueryKey({ ticket_id: ticketId }),
  });
  reactQueryClient.invalidateQueries({
    queryKey: getGetETSTicketRatingQueryKey({ ticket_id: ticketId }),
  });
};

export const useUploadETSAttachment = () => {
  const { mutateAsync, isPending } = useUploadETSTicketAttachmentsMutation();

  const upload = async (ticketId: string, attachment: File | { webPath: string; name: string; type: string }) => {
    let file = attachment;

    if (!(file instanceof File)) {
      file = await createFileFromUrl(file.webPath, file.name, file.type);
    }

    return mutateAsync({
      path: {
        ticket_id: ticketId,
      },
      form: {
        // @ts-expect-error OpenAPI annotates files as strings ¯\_(ツ)_/¯
        file: [file],
      },
    });
  };

  return { upload, isPending };
};

export const useUploadETSAttachments = () => {
  const { upload, isPending } = useUploadETSAttachment();

  const uploadFiles = async (ticketId: string, files: File[]) => {
    const attachmentsPromises = files.map((file) => upload(ticketId, file));
    await Promise.all(attachmentsPromises);
  };

  return { uploadFiles, isPending };
};
